import React, { useState, useRef, useEffect } from 'react';
import { Edit2, Check, X } from 'lucide-react';
import { WorkshopEditabilityContext } from './EditableCell';

interface EditableCurrencyCellProps {
  value: string | number | null;
  unit: 'EUR' | 'KEUR' | 'MEUR';
  workshopId: number;
  valueFieldName: string;
  unitFieldName: string;
  onSave: (workshopId: number, updates: Record<string, any>) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  displayFormatter?: (value: any, unit: string) => string;
}

const EditableCurrencyCell: React.FC<EditableCurrencyCellProps> = ({
  value,
  unit,
  workshopId,
  valueFieldName,
  unitFieldName,
  onSave,
  disabled = false,
  placeholder = '',
  className = '',
  displayFormatter
}) => {
  const inheritedCanEdit = React.useContext(WorkshopEditabilityContext);
  const isDisabled = disabled || !inheritedCanEdit;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [editUnit, setEditUnit] = useState(unit || 'EUR');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);

  // Format display value
  const displayValue = displayFormatter ? displayFormatter(value, unit) : `${value || '0'} ${unit}`;

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && valueInputRef.current) {
      valueInputRef.current.focus();
      valueInputRef.current.select();
    }
  }, [isEditing]);

  // Reset edit values when props change
  useEffect(() => {
    setEditValue(value || '');
    setEditUnit(unit || 'EUR');
  }, [value, unit]);

  const handleEdit = () => {
    if (!isDisabled) {
      setIsEditing(true);
      setError(null);
    }
  };

  const handleSave = async () => {
    // Don't save if no change
    if (editValue === value && editUnit === unit) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Convert value to number if it's not empty
      let processedValue = editValue;
      if (editValue !== '' && editValue !== null) {
        processedValue = parseFloat(editValue.toString());
        if (isNaN(processedValue)) {
          throw new Error('Invalid number format');
        }
      }

      // Prepare updates object with both value and unit
      const updates: Record<string, any> = {};
      updates[valueFieldName] = processedValue;
      updates[unitFieldName] = editUnit;

      await onSave(workshopId, updates);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save currency value:', error);
      setError('Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setEditUnit(unit || 'EUR');
    setIsEditing(false);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleDoubleClick = () => {
    handleEdit();
  };

  if (isEditing) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center space-x-1">
          <input
            ref={valueInputRef}
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            placeholder={placeholder}
            className="text-sm bg-white border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0 flex-1"
            style={{ width: '80px' }}
          />
          
           <select
             value={editUnit}
             onChange={(e) => setEditUnit(e.target.value as 'EUR' | 'KEUR' | 'MEUR')}
             onKeyDown={handleKeyDown}
             disabled={isLoading}
             className="text-sm bg-white border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[70px]"
           >
            <option value="EUR">EUR</option>
            <option value="KEUR">KEUR</option>
            <option value="MEUR">MEUR</option>
          </select>
          
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
            title="Save"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
            title="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        {error && (
          <div className="absolute top-full left-0 mt-1 text-xs text-red-600 bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className={`group relative cursor-pointer ${className}`}
      onDoubleClick={handleDoubleClick}
      title={`Double-click to edit${isDisabled ? ' (read only)' : ''}`}
    >
      <div className="flex items-center justify-between">
        <span className={`${!displayValue && 'text-gray-400 italic'}`}>
          {displayValue || placeholder || 'Click to edit'}
        </span>
        
        {!isDisabled && (
          <button
            onClick={handleEdit}
            disabled={isLoading}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            title="Edit"
          >
            <Edit2 className="h-3 w-3" />
          </button>
        )}
      </div>
      
      {error && (
        <div className="absolute top-full left-0 mt-1 text-xs text-red-600 bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
          {error}
        </div>
      )}
    </div>
  );
};

export default EditableCurrencyCell;
